import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
    Avatar,
    Badge,
    Button,
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    Navbar,
    NavbarDivider,
    NavbarItem,
    NavbarLabel,
    NavbarSection,
    NavbarSpacer,
    Sidebar,
    SidebarBody,
    SidebarDivider,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
    StackedLayout,
} from "components";
import { PrimaryButton } from "src/components/primary-button";
import { useTranslation } from "react-i18next";
import { useAccount } from "src/context/account.tsx";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { ArchiveBoxIcon, QueueListIcon } from "@heroicons/react/24/solid";
import {
    ArrowDownTrayIcon,
    ArrowLeftStartOnRectangleIcon,
    BugAntIcon,
    CameraIcon,
    GlobeAltIcon,
    HeartIcon,
    HomeIcon,
    RectangleStackIcon,
    QuestionMarkCircleIcon,
    ShieldCheckIcon,
    SparklesIcon,
    TrophyIcon,
    UserIcon,
} from "@heroicons/react/20/solid";
import { Role } from "src/api/generated";
import { Suspense, useMemo, useState } from "react";
import { useInstall } from "src/context/install-context";
import { TournamentParticipationEntry } from "src/components/tournament-participation-entry";
import { AppFooter } from "src/components/app-footer";
import { BugReportDialog } from "src/components/bug-report-dialog";
import { ShortcutHelpDialog } from "src/components/shortcut-help-dialog";
import { UnreadDot } from "src/components/unread-dot";
import { useUnreadReleaseNotes } from "src/utils/use-release-notes";
import { ChromeProvider } from "src/context/chrome-context";
import type { Chrome } from "src/context/chrome-context";
import { ShortcutHelpProvider } from "src/context/shortcut-help-context";
import type { ShortcutHelp, ShortcutRow } from "src/context/shortcut-help-context";
import { useFullscreen } from "src/utils/use-fullscreen";
import { useShortcuts } from "src/utils/use-shortcuts";
import { useWatchListAlarms } from "src/utils/use-watch-list-alarms";
import type { ReportStart } from "src/utils/diagnostics";

/** Where a Bug report from the menu starts */
const MENU_START: ReportStart = { kind: "menu" };

export const Route = createFileRoute("/_menu")({
    component: RouteComponent,
});

/**
 * The app chrome: top navbar on desktop, slide-out on mobile.
 *
 * Pathless, so it adds no url segment — only the sections nested under it get the chrome.
 *
 * @returns the chrome around the current section
 */
function RouteComponent() {
    const [t] = useTranslation("menu");
    const [tg] = useTranslation();
    const [helping, setHelping] = useState(false);
    const [reporting, setReporting] = useState(false);

    const navigate = useNavigate();

    const me = useAccount();
    const loggedIn = me.account !== null;
    const admin = me.account?.role === Role.Admin;
    const known = !me.loading;

    const install = useInstall();
    // A page that has taken the whole screen keeps it: the navbar would be a
    // strip of browser back in the middle of a game.
    const { active: fullscreen } = useFullscreen();
    const [pageBare, setPageBare] = useState(false);
    const chrome = useMemo<Chrome>(() => ({ bare: pageBare, setBare: setPageBare }), [pageBare]);

    // Workspaces get the whole window: the deck builder needs room for a
    // hundred cards, while the table counter divides every available
    // centimetre between its players. Reading pages stay in a bounded column.
    const path = useRouterState({ select: (state) => state.location.pathname });
    const building = /^\/decks\/[^/]+/.test(path) || /^\/game-utils(?:\/|$)/.test(path);
    const authPage = path.startsWith("/auth/");
    const [pageShortcuts, setPageShortcuts] = useState<Array<ShortcutRow> | null>(null);
    const shortcuts = pageShortcuts ?? shortcutsFor(path, t, tg);
    const shortcutHelp = useMemo<ShortcutHelp>(
        () => ({ open: helping, show: () => setHelping(true), register: setPageShortcuts }),
        [helping],
    );
    const alarms = useWatchListAlarms(loggedIn, path);
    const unreadReleaseNotes = useUnreadReleaseNotes();

    useShortcuts(
        {
            "?": () => {
                if (shortcuts.length > 0) setHelping((open) => !open);
            },
        },
        true,
        true,
    );

    return (
        <ChromeProvider value={chrome}>
            <ShortcutHelpProvider value={shortcutHelp}>
                <StackedLayout
                    navCollapseBelow={"sm"}
                    contentWidth={building ? "full" : "wide"}
                    bare={fullscreen || pageBare}
                    footer={<AppFooter />}
                    navbar={
                        <Navbar className={"max-lg:gap-2"}>
                            {/* Three tiers, because the app is used half-screen and as an installed
                        pwa far more often than full-width: from `lg` the sections carry their
                        labels, between `sm` and `lg` they shrink to icons (the labels stay for
                        screen readers and as tooltips), and only below `sm` do they move into
                        the slide-over. The labelled row waits for `lg` rather than `md` because
                        german, the longer locale, needs ~665px for it alone, and a tablet held
                        upright — an ipad mini is 768px across — has the help entry, the install
                        prompt and the avatar to fit beside it. The tighter gaps below `lg` are
                        what keeps the icon row roomy for those. */}
                            <NavbarSection className={"max-lg:gap-1 max-sm:hidden"}>
                                <NavbarItem href={"/home"} title={t("label.home")}>
                                    <HomeIcon />
                                    <NavbarLabel className={"max-lg:sr-only"}>{t("label.home")}</NavbarLabel>
                                </NavbarItem>
                                <NavbarItem href={"/global/decks"} title={t("label.decks")}>
                                    <GlobeAltIcon />
                                    <NavbarLabel className={"max-lg:sr-only"}>{t("label.decks")}</NavbarLabel>
                                </NavbarItem>
                                <NavbarItem href={"/tournaments"} title={t("label.tournaments")}>
                                    <TrophyIcon />
                                    <NavbarLabel className={"max-lg:sr-only"}>{t("label.tournaments")}</NavbarLabel>
                                </NavbarItem>
                                <NavbarItem href={"/game-utils"} title={t("label.game-utils")}>
                                    <HeartIcon />
                                    <NavbarLabel className={"max-lg:sr-only"}>{t("label.game-utils")}</NavbarLabel>
                                </NavbarItem>
                                {/* The scanner's own front page, not the camera. That page says
                                which collection is being filled, and it starts loading the
                                catalogue while the button to the camera is still being read —
                                going straight to the viewfinder skips both. */}
                                <NavbarItem href={"/scan"} title={t("label.scan")}>
                                    <CameraIcon />
                                    <NavbarLabel className={"max-lg:sr-only"}>{t("label.scan")}</NavbarLabel>
                                </NavbarItem>
                            </NavbarSection>
                            {known && loggedIn && (
                                <>
                                    <NavbarDivider className={"max-sm:hidden"} />
                                    <NavbarSection className={"max-lg:gap-1 max-sm:hidden"}>
                                        <NavbarItem href={"/decks"} title={t("label.my-decks")}>
                                            <RectangleStackIcon />
                                            <NavbarLabel className={"max-lg:sr-only"}>
                                                {t("label.my-decks")}
                                            </NavbarLabel>
                                        </NavbarItem>
                                        <NavbarItem href={"/collections"} title={t("label.collection")}>
                                            <ArchiveBoxIcon />
                                            <NavbarLabel className={"max-lg:sr-only"}>
                                                {t("label.collection")}
                                            </NavbarLabel>
                                        </NavbarItem>
                                        <NavbarItem href={"/watch-lists"} title={t("label.watch-lists")}>
                                            <QueueListIcon />
                                            <NavbarLabel className={"max-lg:sr-only"}>
                                                {t("label.watch-lists")}
                                            </NavbarLabel>
                                            {alarms > 0 && (
                                                <Badge color={"amber"} className={"ml-1"}>
                                                    {alarms}
                                                </Badge>
                                            )}
                                        </NavbarItem>
                                        {admin && (
                                            <NavbarItem href={"/admin"} title={t("label.admin")}>
                                                <ShieldCheckIcon />
                                                <NavbarLabel className={"max-lg:sr-only"}>
                                                    {t("label.admin")}
                                                </NavbarLabel>
                                            </NavbarItem>
                                        )}
                                    </NavbarSection>
                                </>
                            )}
                            <NavbarSpacer />
                            <NavbarSection>
                                <NavbarItem href={"/whats-new"} title={t("label.whats-new")}>
                                    <SparklesIcon />
                                    <NavbarLabel className={"sr-only"}>{t("label.whats-new")}</NavbarLabel>
                                    {unreadReleaseNotes && (
                                        <UnreadDot label={t("accessibility.unread-release-notes")} />
                                    )}
                                </NavbarItem>
                                {/* Shortcuts need a keyboard, which a touch screen rarely has. */}
                                <NavbarItem
                                    onClick={() => setHelping(true)}
                                    title={t("heading.shortcuts")}
                                    className={"pointer-coarse:hidden"}
                                >
                                    <QuestionMarkCircleIcon />
                                    <NavbarLabel className={"sr-only"}>{t("heading.shortcuts")}</NavbarLabel>
                                </NavbarItem>
                            </NavbarSection>
                            {/* Labelled and on every width, unlike the sections next to it: the entry
                        is the only place offering the install, and an icon alone does not say
                        what it does. It disappears once the app runs from the home screen. */}
                            {install.canInstall && (
                                <NavbarSection>
                                    <NavbarItem onClick={install.install} title={tg("button.install-app")}>
                                        <ArrowDownTrayIcon />
                                        <NavbarLabel className={"whitespace-nowrap"}>
                                            {tg("button.install-app")}
                                        </NavbarLabel>
                                    </NavbarItem>
                                </NavbarSection>
                            )}
                            {!known ? null : loggedIn ? (
                                <NavbarSection>
                                    <Dropdown>
                                        <DropdownButton plain={true}>
                                            <Avatar
                                                className={"size-6"}
                                                initials={me.account?.username.substring(0, 2)}
                                                alt={`Avatar of ${me.account?.username}`}
                                            />
                                            <ChevronDownIcon className={"size-3"} />
                                        </DropdownButton>
                                        <DropdownMenu anchor={"bottom end"}>
                                            <DropdownItem href={"/profile"}>
                                                <UserIcon />
                                                <DropdownLabel>{t("label.profile-settings")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setReporting(true)}>
                                                <BugAntIcon />
                                                <DropdownLabel>{tg("button.report-bug")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={async () => {
                                                    await me.logout();
                                                    await navigate({ to: "/" });
                                                }}
                                            >
                                                <ArrowLeftStartOnRectangleIcon />
                                                <DropdownLabel>{t("label.logout")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </NavbarSection>
                            ) : authPage ? null : (
                                // The auth pages are the form and link the other one themselves. On a
                                // phone the bar only fits one button: signing in is what returning
                                // players come for, signing up stays in the slide-over and in the
                                // home page's call to action.
                                <NavbarSection>
                                    <Button outline={true} href={"/auth/login"} className={"whitespace-nowrap"}>
                                        {t("label.login")}
                                    </Button>
                                    <PrimaryButton href={"/auth/signup"} className={"whitespace-nowrap max-sm:hidden"}>
                                        {t("label.sign-up")}
                                    </PrimaryButton>
                                </NavbarSection>
                            )}
                        </Navbar>
                    }
                    sidebar={
                        <Sidebar>
                            <SidebarBody>
                                <SidebarSection>
                                    <SidebarItem href={"/home"}>
                                        <HomeIcon />
                                        <SidebarLabel>{t("label.home")}</SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={"/global/decks"}>
                                        <GlobeAltIcon />
                                        <SidebarLabel>{t("label.decks")}</SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={"/tournaments"}>
                                        <TrophyIcon />
                                        <SidebarLabel>{t("label.tournaments")}</SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={"/game-utils"}>
                                        <HeartIcon />
                                        <SidebarLabel>{t("label.game-utils")}</SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={"/scan"}>
                                        <CameraIcon />
                                        <SidebarLabel>{t("label.scan")}</SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>

                                {known && loggedIn && (
                                    <>
                                        <SidebarDivider />
                                        <SidebarSection>
                                            <SidebarItem href={"/decks"}>
                                                <RectangleStackIcon />
                                                <SidebarLabel>{t("label.my-decks")}</SidebarLabel>
                                            </SidebarItem>
                                            <SidebarItem href={"/collections"}>
                                                <ArchiveBoxIcon />
                                                <SidebarLabel>{t("label.collection")}</SidebarLabel>
                                            </SidebarItem>
                                            <SidebarItem href={"/watch-lists"}>
                                                <QueueListIcon />
                                                <SidebarLabel>{t("label.watch-lists")}</SidebarLabel>
                                                {alarms > 0 && <Badge color={"amber"}>{alarms}</Badge>}
                                            </SidebarItem>
                                            {admin && (
                                                <SidebarItem href={"/admin"}>
                                                    <ShieldCheckIcon />
                                                    <SidebarLabel>{t("label.admin")}</SidebarLabel>
                                                </SidebarItem>
                                            )}
                                        </SidebarSection>
                                    </>
                                )}

                                <SidebarSpacer />

                                <SidebarSection>
                                    <SidebarItem href={"/whats-new"}>
                                        <SparklesIcon />
                                        <SidebarLabel>{t("label.whats-new")}</SidebarLabel>
                                        {unreadReleaseNotes && (
                                            <UnreadDot label={t("accessibility.unread-release-notes")} />
                                        )}
                                    </SidebarItem>
                                    <SidebarItem onClick={() => setHelping(true)} className={"pointer-coarse:hidden"}>
                                        <QuestionMarkCircleIcon />
                                        <SidebarLabel>{t("heading.shortcuts")}</SidebarLabel>
                                    </SidebarItem>
                                    {!known ? null : loggedIn ? (
                                        <>
                                            <SidebarItem href={"/profile"}>
                                                <UserIcon />
                                                <SidebarLabel>{t("label.profile-settings")}</SidebarLabel>
                                            </SidebarItem>
                                            <SidebarItem onClick={() => setReporting(true)}>
                                                <BugAntIcon />
                                                <SidebarLabel>{tg("button.report-bug")}</SidebarLabel>
                                            </SidebarItem>
                                            <SidebarItem
                                                onClick={async () => {
                                                    await me.logout();
                                                    await navigate({ to: "/" });
                                                }}
                                            >
                                                <ArrowLeftStartOnRectangleIcon />
                                                <SidebarLabel>{t("label.logout")}</SidebarLabel>
                                            </SidebarItem>
                                        </>
                                    ) : (
                                        <>
                                            <SidebarItem href={"/auth/login"}>
                                                <SidebarLabel>{t("label.login")}</SidebarLabel>
                                            </SidebarItem>
                                            <SidebarItem href={"/auth/signup"}>
                                                <SidebarLabel>{t("label.sign-up")}</SidebarLabel>
                                            </SidebarItem>
                                        </>
                                    )}
                                </SidebarSection>
                            </SidebarBody>
                        </Sidebar>
                    }
                >
                    {!fullscreen && !pageBare && <TournamentParticipationEntry />}
                    <Suspense>
                        <Outlet />
                    </Suspense>
                </StackedLayout>
                <ShortcutHelpDialog open={helping} shortcuts={shortcuts} onClose={() => setHelping(false)} />
                {loggedIn ? (
                    <BugReportDialog open={reporting} onClose={() => setReporting(false)} start={MENU_START} />
                ) : null}
            </ShortcutHelpProvider>
        </ChromeProvider>
    );
}

/**
 * The shortcuts available on the current page
 *
 * @param path the pathname being looked at
 * @param t the menu namespace's translator, which names the actions
 * @param tg the general translator, for actions named outside a page namespace
 *
 * @returns the rows the help dialog lists, empty for a page without shortcuts
 */
function shortcutsFor(
    path: string,
    t: (key: string, options?: Record<string, unknown>) => string,
    tg: (key: string, options?: Record<string, unknown>) => string,
) {
    if (/^\/decks\/?$/.test(path)) {
        return [
            { keys: "Ctrl/⌘ F", description: t("label.search-decks") },
            { keys: "A", description: t("button.create-deck") },
            { keys: "E", description: t("button.edit-deck") },
            { keys: "S", description: t("button.share-deck") },
            { keys: "Entf", description: t("button.delete-deck") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/decks\/[^/]+\/cards/.test(path)) {
        return [
            { keys: "A", description: t("button.add-cards") },
            { keys: "Ctrl/⌘ F", description: t("label.search-cards") },
            { keys: "V", description: t("label.view") },
            { keys: "G", description: t("label.grouping") },
            { keys: "T", description: t("button.manage-tags") },
            { keys: "1-9", description: t("description.quick-tag") },
            { keys: "P", description: t("button.change-printing") },
            { keys: "F", description: t("button.use-foil") },
            { keys: "X", description: t("button.mark-proxy") },
            { keys: "Ctrl/⌘ Z", description: tg("button.undo") },
            { keys: "Ctrl/⌘ Y", description: tg("button.redo") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/decks\/[^/]+\/advisor/.test(path)) {
        return [
            { keys: "A", description: tg("button.add-hovered-card") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/collections\/?$/.test(path)) {
        return [
            { keys: "Enter", description: t("button.open-collection") },
            { keys: "A", description: t("button.create-collection") },
            { keys: "E", description: t("button.edit-collection") },
            { keys: "S", description: t("button.share-collection") },
            { keys: "Entf", description: t("button.delete-collection") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/collections\/[^/]+\/cards/.test(path)) {
        return [
            { keys: "Enter", description: t("button.inspect-card") },
            { keys: "+ -", description: t("description.shortcut-quantity") },
            { keys: "Entf", description: t("button.delete-entry") },
            { keys: "A", description: t("button.file-cards") },
            { keys: "Ctrl/⌘ F", description: t("label.filter-cards") },
            { keys: "P", description: t("button.change-printing") },
            { keys: "T", description: t("button.manage-tags") },
            { keys: "V", description: t("label.view") },
            { keys: "O", description: t("description.shortcut-sort") },
            { keys: "E", description: t("button.edit-collection") },
            { keys: "S", description: t("button.share-collection") },
            { keys: "1 2 3", description: t("description.shortcut-tabs") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/watch-lists\/?$/.test(path)) {
        return [
            { keys: "Enter", description: tg("button.open-watch-list") },
            { keys: "A", description: tg("button.create-watch-list") },
            { keys: "E", description: tg("button.edit-watch-list") },
            { keys: "Entf", description: tg("button.delete-watch-list") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/watch-lists\/[^/]+/.test(path)) {
        return [
            { keys: "A", description: tg("button.add-watch-card") },
            { keys: "Ctrl/⌘ F", description: tg("button.search-watch-list") },
            { keys: "Enter", description: tg("button.show-watch-copies") },
            { keys: "E", description: tg("button.edit-watch-entry") },
            { keys: "F", description: tg("button.toggle-watch-finish") },
            { keys: "V", description: tg("button.switch-watch-view") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/scan\/?$/.test(path)) {
        return [
            { keys: "S", description: tg("button.start-scanning") },
            { keys: "L", description: tg("label.staged-cards") },
            { keys: "E", description: tg("button.edit-session") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    if (/^\/scan\/staged\/.+/.test(path)) {
        return [
            { keys: "F", description: tg("button.file-session") },
            { keys: "E", description: tg("button.edit-session") },
            { keys: "S", description: tg("button.start-scanning") },
            { keys: "?", description: t("heading.shortcuts") },
        ];
    }
    return [{ keys: "?", description: t("heading.shortcuts") }];
}
